import { useCallback } from 'react';
import { useTaskActions, useSignalR } from '../../../../hooks';
import { mapCommentFromApi } from '@/store/mappers/commentMapper';
import type { CommentResponseDto } from '../../../../types/api/responses';

interface TaskCommentsSignalRProps {
  taskId: string;
}

/**
 * Headless wiring for real-time comment updates over SignalR. Renders nothing;
 * mounted only for authenticated viewers (omitted in read-only public shares).
 */
export const TaskCommentsSignalR = ({ taskId }: TaskCommentsSignalRProps) => {
  const { receiveComment, updateComment, deleteComment } = useTaskActions();

  const handleSignalRCommentAdded = useCallback(
    (dto: CommentResponseDto) => {
      receiveComment(taskId, mapCommentFromApi(dto));
    },
    [taskId, receiveComment]
  );

  const handleSignalRCommentUpdated = useCallback(
    (dto: CommentResponseDto) => {
      updateComment(taskId, dto.id, mapCommentFromApi(dto));
    },
    [taskId, updateComment]
  );

  const handleSignalRCommentDeleted = useCallback(
    (commentId: string) => {
      deleteComment(taskId, commentId);
    },
    [taskId, deleteComment]
  );

  useSignalR({
    taskId,
    onCommentAdded: handleSignalRCommentAdded,
    onCommentUpdated: handleSignalRCommentUpdated,
    onCommentDeleted: handleSignalRCommentDeleted,
  });

  return null;
};
